import React, { FC, lazy, ReactNode, Suspense } from 'react';

import { Container, styled } from '@mui/material';
import { captureException, setTag } from '@sentry/react';
import cloneDeep from 'lodash/cloneDeep';
import { ErrorBoundary } from 'react-error-boundary';

import AccordionsBlock from '~/blocks/AccordionsBlock/AccordionsBlock';
import CoursesBlock from '~/blocks/CoursesBlock/CoursesBlock';
import ImageAndTextBlock from '~/blocks/ImageAndTextBlock/ImageAndTextBlock';
import LetsTalkBlock from '~/blocks/LetsTalkBlock/LetsTalkBlock';
import RequestADemo from '~/blocks/RequestADemo/RequestADemo';
import RequestAFullCourseListBlock from '~/blocks/RequestAFullCourseListBlock/RequestAFullCourseListBlock';
import Testimonials from '~/blocks/Testimonials/Testimonials';
import AreasBlock from '~/components/AreasBlock/AreasBlock';
import CardsBlock from '~/components/CardsBlock/CardsBlock';
import ContactDetailsBlock from '~/components/ContactDetailsBlock/ContactDetailsBlock';
import ContactFormBlock from '~/components/ContactFormBlock/ContactFormBlock';
import ContentListBlock from '~/components/ContentListBlock/ContentListBlock';
import CtaBlock from '~/components/CtaBlock/CtaBlock';
import ExpertsBlock from '~/components/ExpertsBlock/ExpertsBlock';
import HTMLBlock from '~/components/HTMLBlock/HTMLBlock';
import HalfWidthImgBlock from '~/components/HalfWidthImgBlock/HalfWidthImgBlock';
import ImageHeadingTextBlock from '~/components/ImageHeadingTextBlock/ImageHeadingTextBlock';
import ImageTextArrayBlock from '~/components/ImageTextArrayBlock/ImageTextArrayBlock';
import ImageTextArrayBlockSized from '~/components/ImageTextArrayBlock/ImageTextArrayBlockSized';
import ImageWithTabsBlock from '~/components/ImageWithTabsBlock/ImageWithTabsBlock';
import IntroBlock from '~/components/IntroBlock/IntroBlock';
import JSBlock from '~/components/JSBlock/JSBlock';
import MeetTheTeam from '~/components/MeetTheTeam/MeetTheTeam';
import NewsBlock from '~/components/NewsBlock/NewsBlock';
import PartnersBlock from '~/components/PartnersBlock/PartnersBlock';
import PathwaysListBlock from '~/components/PathwaysListBlock/PathwaysListBlock';
import QuotesBlock from '~/components/QuotesBlock/QuotesBlock';
import SeoBlock from '~/components/SeoBlock/SeoBlock';
import TabsSliderBlock from '~/components/TabsSliderBlock/TabsSliderBlock';
import TrustpilotBlock from '~/components/TrustpilotBlock/TrustpilotBlock';
import VideosBlock from '~/components/VideosBlock/VideosBlock';
import WeAreBlock from '~/components/WeAreBlock/WeAreBlock';
import WhyUsWideImgBlock from '~/components/WhyUsWideImgBlock/WhyUsWideImgBlock';
import useAnchor from '~/hooks/useAnchor';
import useAuth from '~/hooks/useAuth';
import useQueriesInProgress from '~/hooks/useQueriesInProgress';
import {
  Block,
  BlockType,
  ImageArrayBlockImageType,
  ImagesAndTextArrayBlock,
  IntroImageBlockViewType,
} from '~/types';
import CustomBlockError from '~/utils/errors/CustomBlockError';
import { toBoolean } from '~/utils/toBoolean';

const SearchBlock = lazy(() => import('../SearchBlock/SearchBlock'));
const PricePage = lazy(() => import('../../pages/PricePage/PricePage'));

interface CustomBlocksProps {
  blocks: (Block | null)[] | null;
  preventLoader?: boolean;
  blocksLoading?: boolean;
  canonicalUrl?: string;
}

const isImageAndTextArrayBlockData = (
  block: Block,
): block is ImagesAndTextArrayBlock & { shouldStartFromImage: boolean } =>
  block.type === BlockType.ImagesAndTextArrayBlock;

const CustomBlocks: FC<CustomBlocksProps> = ({
  blocks,
  preventLoader,
  blocksLoading,
  canonicalUrl,
}) => {
  const { isInitialized } = useAuth();
  const blocksNotLoaded = !isInitialized || blocksLoading;
  const blocksData = cloneDeep(blocks?.filter(toBoolean<Block>) || []);

  // For "images-and-text-array-block" we need to check their ranking
  // to show them differently if they go one after each other
  let counter = 0;
  let startFromImage = false;

  const { inProgress } = useQueriesInProgress();
  useAnchor(null, { skip: inProgress, delay: 0 });

  blocksData.forEach((blockData) => {
    if (isImageAndTextArrayBlockData(blockData)) {
      if (counter === 0 && blockData?.items?.length && blockData.items[0]?.type === 'image') {
        startFromImage = true;
      }
      counter += 1;
      blockData.shouldStartFromImage = startFromImage ? Boolean(counter % 2) : !(counter % 2);
    } else {
      counter = 0;
      startFromImage = false;
    }
  });

  return (
    <StyledContainer blocksNotLoaded={blocksNotLoaded} maxWidth={false}>
      {blocksData.map((blockData, index) => {
        const props = {
          preventLoader,
          canonicalUrl,
        };

        const getComponent = () => {
          const typename = blockData.__typename;

          switch (typename) {
            case 'IntroVideoBlock':
              return <IntroBlock data={blockData} {...props} />;
            case 'IntroImageBlock':
              return blockData.view_type === IntroImageBlockViewType.Intro ? (
                <IntroBlock video={false} data={blockData} {...props} />
              ) : blockData.view_type === IntroImageBlockViewType.Inline ? (
                <ImageAndTextBlock data={blockData} {...props} />
              ) : null;
            case 'ImageArrayBlock':
              return blockData.type_image === ImageArrayBlockImageType.FixedImage ? (
                <ImageHeadingTextBlock data={blockData} {...props} />
              ) : blockData.type_image === ImageArrayBlockImageType.WideImage ? (
                <WhyUsWideImgBlock data={blockData} {...props} />
              ) : null;
            case 'ImagesAndTextArrayBlock':
              return blockData.type_block === 'sized' ? (
                <ImageTextArrayBlockSized data={blockData} {...props} />
              ) : (
                <ImageTextArrayBlock data={blockData} {...props} />
              );
            case 'PartnersBlock':
              return <PartnersBlock data={blockData} {...props} />;
            case 'NewsBlock':
              return <NewsBlock data={blockData} {...props} />;
            case 'VideoListBlock':
              return <VideosBlock data={blockData} {...props} />;
            case 'PathwayListBlock':
              return <PathwaysListBlock data={blockData} {...props} />;
            case 'CatalogueOfCategoriesBlock':
              return <AreasBlock data={blockData} {...props} />;
            case 'ExpertsListBlock':
              return <ExpertsBlock data={blockData} {...props} />;
            case 'ContactDetailsBlock':
              return <ContactDetailsBlock data={blockData} {...props} />;
            case 'ContactFormBlock':
              return <ContactFormBlock data={blockData} {...props} />;
            case 'SeoBlock':
              return <SeoBlock data={blockData} {...props} />;
            case 'PricingBlock':
              return (
                <Suspense>
                  <PricePage plansType="individual" data={blockData} isBlock={true} {...props} />
                </Suspense>
              );
            case 'HTMLBlock':
              return <HTMLBlock data={blockData} {...props} />;
            case 'JSBlock':
              return <JSBlock data={blockData} {...props} />;
            case 'QuotesListBlock':
              return <QuotesBlock data={blockData} {...props} />;
            case 'CTABlock':
              return <CtaBlock data={blockData} {...props} />;
            case 'ForEnterprise':
              return <ImageWithTabsBlock data={blockData} {...props} />;
            case 'AllProfessionals':
              return <HalfWidthImgBlock data={blockData} {...props} />;
            case 'TrustpilotBlock':
              return <TrustpilotBlock data={blockData} {...props} />;
            case 'ImageSliderWithText':
              return <TabsSliderBlock data={blockData} {...props} />;
            case 'WhatBusinessStand':
              return <CardsBlock data={blockData} {...props} />;
            case 'MeetTheTeam':
              return <MeetTheTeam data={blockData} {...props} />;
            case 'WeAreBlock':
              return <WeAreBlock data={blockData} {...props} />;
            case 'TestimonialsBlock':
              return <Testimonials data={blockData} {...props} />;
            case 'FAQ':
              return <AccordionsBlock data={blockData} {...props} />;
            case 'RequestDemo':
              return <RequestADemo data={blockData} {...props} />;
            case 'CourseListBlock':
              return <CoursesBlock data={blockData} {...props} />;
            case 'SearchBlock':
              return (
                <Suspense>
                  <SearchBlock data={blockData} {...props} />
                </Suspense>
              );
            case 'ContentItemListBlock':
              return <ContentListBlock data={blockData} {...props} />;
            case 'LetUsTalk':
              return <LetsTalkBlock data={blockData} {...props} />;
            case 'RequestCourseListBlock':
              return <RequestAFullCourseListBlock data={blockData} {...props} />;
            case 'EngagementMetricBlock':
            case undefined:
              return null;
            default:
              return null;
          }
        };

        const Component: ReactNode = getComponent();

        if (!Component) {
          return null;
        }

        return (
          <ErrorBoundary
            key={index}
            fallback={<div />}
            onError={(error) => {
              setTag(
                'block_type',
                blockData.__typename === 'ImageArrayBlock'
                  ? blockData?.type_image
                  : blockData?.type,
              );
              captureException(new CustomBlockError(error?.message));
            }}
          >
            {Component}
          </ErrorBoundary>
        );
      })}
    </StyledContainer>
  );
};

export default CustomBlocks;

const StyledContainer = styled(Container, {
  shouldForwardProp: (prop) => prop !== 'blocksNotLoaded',
})<{ blocksNotLoaded?: boolean }>`
  flex: 1;
  flex-grow: 1;
  overflow: hidden;
  min-height: ${({ blocksNotLoaded }) => (blocksNotLoaded ? '100vh' : 'unset')};

  && {
    padding: 0;
  }
`;
